import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import WifiIcon from '@mui/icons-material/Wifi';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Tooltip,
} from '@mui/material';
import dayjs from 'dayjs';
import update from 'immutability-helper';
import orderBy from 'lodash/orderBy';
import range from 'lodash/range';
import PopupState, {bindMenu, bindTrigger} from 'material-ui-popup-state';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  Label,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {useAppDispatch, useAppSelector} from '../../../hooks/redux';
import {useRefreshInterval} from '../../../hooks/refreshInterval';
import useShiftSelect, {isShiftClick} from '../../../hooks/useShiftSelect';
import {
  WifiPointHistory,
  WifiPointHistoryListQuery,
  WifiPointHistoryListResponse,
} from '../../../interfaces/WifiPointHistory';
import reduxActions from '../../../redux/actions';
import reduxSelectors from '../../../redux/selectors';
import {eventIconsLegacy} from '../../../utils/event-icons';
import {
  handleChangeTab,
  handleCloseTab,
  handleOpenTab,
} from '../../../utils/tab';
import {AdaptiveButtonGroup} from '../../common/AdaptiveButtonGroup';
import {AutoRefreshSelect} from '../../common/AutoRefreshSelect';
import DataGrid, {DataGridColumn, DataGridRef} from '../../common/DataGrid';
import {ResizableColumns} from '../../common/ResizableColumns';
import TabLabel from '../../common/TabLabel';
import WifiPointHistoryTrackingReportExportButton from '../../communication-node/WifiPointHistoryTrackingReportExportButton';
import {DateRangeSelect} from '../../selectors/DateRangeSelect';
import {DashboardPanelTitleSlot} from '../DashboardPanelTitleSlot';

interface Props {
  value?: WifiPointHistoryReportsData;
  onUpdate?: (value?: WifiPointHistoryReportsData) => void;
}

const DEFAULT_SHOWN_FIELDS = ['no_heartbeat', 'ping', 'response', 'latency'];

interface WifiPointHistoryReportsDataTab {
  id: number;
  refreshInterval?: number | null;
  selectedIds?: number[];
  selectAll: boolean;
  params?: {
    date_start?: string;
    date_end?: string;
    page?: number;
    limit?: number;
    order?: string;
    dir?: 'ASC' | 'DESC';
  };
  columnWidths: Record<string, number>;
  columnOrder?: string[];
  shownFields?: string[];
}
export interface WifiPointHistoryReportsData {
  activeId?: number;
  openedItems: WifiPointHistoryReportsDataTab[];
}

export const getWifiPointHistoryReportsData =
  (): WifiPointHistoryReportsData => ({
    activeId: undefined,
    openedItems: [],
  });

const getWifiPointHistoryReportsDataTab = (
  id: number
): WifiPointHistoryReportsDataTab => ({
  id,
  selectAll: true,
  params: {
    date_start: dayjs().format('YYYY-MM-DD'),
    date_end: dayjs().format('YYYY-MM-DD'),
  },
  columnWidths: {},
  columnOrder: undefined,
  shownFields: DEFAULT_SHOWN_FIELDS,
});

export const WifiPointHistoryReports = ({value, onUpdate}: Props) => {
  const reduxDispatch = useAppDispatch();

  const config = useMemo(
    () => value ?? getWifiPointHistoryReportsData(),
    [value]
  );

  const isOpenAwayFromConnectView = useMemo(() => {
    return (
      location.pathname.includes('/panels/') ||
      !document.getElementById('connect-view-panel')
    );
  }, [location, value]);

  const openedItemIndex = useMemo(
    () =>
      (config.activeId
        ? config.openedItems.findIndex((i) => i.id === config.activeId)
        : null) ?? config.openedItems.length - 1,
    [config.activeId, config.openedItems]
  );

  const openedItem = useMemo(
    () =>
      openedItemIndex !== -1
        ? config.openedItems[openedItemIndex]
          ? config.openedItems[openedItemIndex]
          : getWifiPointHistoryReportsDataTab(config.activeId as number)
        : null,
    [openedItemIndex, config.openedItems]
  );

  const nodes = useAppSelector(({assets}) =>
    assets.nodes.filter(
      (i) =>
        (i.node_type === 'wifi_point' || i.node_type === 'bluetooth_node') &&
        i.ack === '1'
    )
  );

  const tabNames = useMemo(
    () =>
      config.openedItems.map(
        (o) => nodes.find((i) => i.wifi_id === o.id)?.name
      ),
    [config.openedItems, nodes]
  );

  // get fresh assets
  useEffect(() => {
    reduxDispatch(reduxActions.assets.fetchNodes);
  }, [config.openedItems]);

  /*********/
  /* fetch */
  /*********/
  const [fetchedData, setFetchedData] =
    useState<WifiPointHistoryListResponse>();
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);

  const params = useMemo<WifiPointHistoryListQuery | null>(
    () =>
      openedItem?.id
        ? {
            date_start:
              openedItem.params?.date_start ?? dayjs().format('YYYY-MM-DD'),
            date_end:
              openedItem.params?.date_end ?? dayjs().format('YYYY-MM-DD'),
            page: openedItem.params?.page ?? 0,
            limit: openedItem.params?.limit ?? 25,
            order: openedItem.params?.order ?? 'timestamp',
            dir: openedItem.params?.dir ?? 'DESC',
            id: openedItem.id,
          }
        : null,
    [openedItem?.id, openedItem?.params]
  );

  const fetchData = useCallback(
    async (params: WifiPointHistoryListQuery) => {
      setFetchedInProgress(true);
      setFetchedErrors([]);
      try {
        const resp = await API.get<WifiPointHistoryListResponse>(
          `${apiBaseUrl}/node/${openedItem?.id}/wifi-point/events`,
          {params}
        );
        setFetchedData(resp.data);
        // collect all IDs
        if (openedItem && openedItem.selectAll) {
          openedItem.selectedIds = resp.data.items.map((el) => el.id);
        }
      } catch (error: any) {
        const messages = getMessagesFromApiError(error, 'STRA_0cf54c98');
        setFetchedErrors(messages);
      }
      setFetchedInProgress(false);
    },
    [params]
  );

  useEffect(() => {
    if (params) {
      fetchData(params);
    }
  }, [params]);

  useEffect(() => {
    if (!openedItem) {
      setFetchedData(undefined);
    }
  }, [openedItem]);

  /****************/
  /* auto refresh */
  /****************/
  const callFetchData = useCallback(() => {
    if (params) {
      fetchData(params);
    }
  }, [params]);
  useRefreshInterval(callFetchData, openedItem?.refreshInterval);

  /*************/
  /* data grid */
  /*************/
  const shownFields = useMemo(
    () => openedItem?.shownFields ?? DEFAULT_SHOWN_FIELDS,
    [openedItem?.shownFields]
  );

  const dataGridRef = useRef<DataGridRef>(null);
  const rows = fetchedData?.items ?? [];
  const columns: DataGridColumn<WifiPointHistory>[] = [
    {
      field: 'select',
      type: 'select',
      width: 40,
      hideable: false,
      renderHeader: () => (
        <Checkbox
          color="primary"
          disabled={rows.length === 0}
          checked={selectedItems.length > 0 && selectedAll}
          indeterminate={selectedItems.length > 0 && !selectedAll}
          onChange={() => toggleSelectAllItems()}
        />
      ),
      renderCell: ({row, visibleRows}) => (
        <Checkbox
          color="primary"
          checked={selectedItems.includes(row.id)}
          onChange={(event) =>
            toggleSelectItem(
              row.id,
              isShiftClick(event),
              visibleRows.map((visibleRow) => visibleRow.id)
            )
          }
        />
      ),
    },
    {
      field: 'no_heartbeat',
      headerName: 'No Heartbeat',
      width: 40,
      doNotExport: true,
      renderHeader: () => (
        <Tooltip title="No Heartbeat">
          <WifiIcon />
        </Tooltip>
      ),
      sortable: false,
      valueGetter: ({row}) => row.current_connection_status,
      renderCell: ({row}) => {
        if (row.current_connection_status === 'disconnected') {
          return (
            <Tooltip title="Disconnected">
              {eventIconsLegacy.nodes.wifiDisconnected}
            </Tooltip>
          );
        }
      },
    },
    {
      field: 'id',
      sortable: true,
      width: 60,
      resizeable: true,
      headerName: 'ID',
    },
    {
      field: 'ping',
      sortable: true,
      width: 180,
      resizeable: true,
      headerName: 'Last Ping',
    },
    {
      field: 'response',
      sortable: true,
      width: 180,
      resizeable: true,
      headerName: 'Last Response',
    },
    {
      field: 'latency',
      headerName: 'Latency',
      width: 80,
      resizeable: true,
      sortable: true,
    },
  ];

  const exportFields = useMemo(() => {
    return columns
      .filter((col) => !!col.headerName && !col.doNotExport)
      .map((col) => ({
        field: col.field,
        label: col.headerName,
        hidden: !shownFields?.includes(col.field),
      }));
  }, [shownFields]);

  /*******************/
  /* multiple select */
  /*******************/
  const selectedItems = openedItem?.selectedIds ?? [];

  const selectedRows = useMemo(
    () => rows.filter((i) => selectedItems?.includes(i.id)),
    [rows, selectedItems]
  );

  const selectedAll = useMemo(
    () => rows.length === selectedRows.length,
    [rows, selectedRows]
  );

  const getNextSelectedIds = useShiftSelect<number>();

  const toggleSelectItem = (
    id: number,
    shiftKey: boolean,
    visibleIds: number[]
  ) => {
    if (openedItem) {
      const nextSelectedIds = getNextSelectedIds({
        id,
        visibleIds,
        selectedIds: selectedItems ?? [],
        shiftKey,
      });
      onUpdate?.(
        update(config, {
          openedItems: {
            [openedItemIndex]: {
              $set: {
                ...openedItem,
                selectedIds: nextSelectedIds,
                selectAll: nextSelectedIds.length === fetchedData?.items.length,
              },
            },
          },
        })
      );
    }
  };

  const selectAll = () => {
    if (openedItem) {
      onUpdate?.(
        update(config, {
          openedItems: {
            [openedItemIndex]: {
              selectedIds: {
                $set: rows?.map((i) => i.id) ?? [],
              },
              selectAll: {
                $set: true,
              },
            },
          },
        })
      );
    }
  };

  const unselectAll = () => {
    if (openedItem) {
      onUpdate?.(
        update(config, {
          openedItems: {
            [openedItemIndex]: {
              selectedIds: {
                $set: [],
              },
              selectAll: {
                $set: false,
              },
            },
          },
        })
      );
    }
  };

  const toggleSelectAllItems = () => {
    if (selectedItems.length >= rows.length) {
      unselectAll();
    } else {
      selectAll();
    }
  };

  const timeFormatter = (timestamp: string) => {
    return dayjs(timestamp).format('HH:mm');
  };

  const dateTimeFormatter = (timestamp: string) => {
    return dayjs(timestamp).format('llll');
  };

  const isDarkMode = useAppSelector(reduxSelectors.app.getIsDarkMode);
  const chartData = useMemo(
    () =>
      orderBy(
        selectedRows.map((i) => ({...i, latency: i.latency ?? 2000})),
        (i) => i.ping
      ),
    [selectedRows]
  );

  const ticks = useMemo(() => {
    if (chartData.length) {
      return range(
        Math.min(...chartData.map((i) => i.latency)) - 1000,
        Math.max(...chartData.map((i) => i.latency)) + 2000,
        1000
      );
    }
  }, [chartData]);

  const openTab = (id: number) => {
    return handleOpenTab(id, config, onUpdate);
  };

  const closeTab = (id: number) => {
    return handleCloseTab(id, config, onUpdate);
  };

  const changeTab = (_: any, id: number) => {
    handleChangeTab(id, config, onUpdate);
  };

  const handleColumnWidthChange = (widths: Record<string, number>) => {
    if (openedItem) {
      onUpdate?.(
        update(config, {
          openedItems: {
            [openedItemIndex]: {
              $set: {
                ...openedItem,
                columnWidths: widths,
              },
            },
          },
        })
      );
    }
  };

  const handleShownFieldsChange = useCallback(
    (fields: string[]) => {
      if (openedItem) {
        onUpdate?.(
          update(config, {
            openedItems: {
              [openedItemIndex]: {
                shownFields: {$set: fields},
              },
            },
          })
        );
      }
    },
    [onUpdate, config, openedItemIndex, openedItem]
  );

  const handleColumnOrderAndShownFieldsChange = useCallback(
    (order: string[], fields: string[]) => {
      if (openedItem) {
        onUpdate?.(
          update(config, {
            openedItems: {
              [openedItemIndex]: {
                columnOrder: {$set: order},
                shownFields: {$set: fields},
              },
            },
          })
        );
      }
    },
    [onUpdate, config, openedItemIndex, openedItem]
  );

  return (
    <>
      <DashboardPanelTitleSlot>
        WiFi/Bluetooth Nodes Tracking Reports
      </DashboardPanelTitleSlot>

      <ResizableColumns
        left={
          <Box
            width="100%"
            height="100%"
            minWidth={400}
            pt={2}
            pb={2}
            pr={6}
            bgcolor={isDarkMode ? 'background.default' : 'grey.100'}
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{left: 20, top: 40}}>
                <XAxis dataKey="ping" tickFormatter={timeFormatter} />
                <YAxis ticks={ticks}>
                  <Label value="Latency (ms)" dx={-30} angle={-90} />
                </YAxis>
                <RechartsTooltip
                  labelStyle={{color: 'black'}}
                  labelFormatter={dateTimeFormatter}
                />
                <Legend />
                <Line dataKey="latency" name="Latency" />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        }
      >
        <Box
          display="flex"
          flexDirection="column"
          height="100%"
          width="100%"
          overflow="hidden"
        >
          <Box
            display="flex"
            flexDirection="column"
            gap={3}
            py={1.5}
            bgcolor={(theme) =>
              theme.palette.mode === 'dark' ? '#2E2E2E' : '#FFF'
            }
          >
            {isOpenAwayFromConnectView && (
              <Box pr={1.5}>
                <PopupState variant="popover" popupId="demo-popup-menu">
                  {(popupState) => (
                    <>
                      <Button variant="outlined" {...bindTrigger(popupState)}>
                        Select an Object
                      </Button>
                      <Menu {...bindMenu(popupState)}>
                        {nodes?.map((it) => (
                          <MenuItem
                            key={it.id}
                            onClick={() => {
                              it?.id && openTab(it.id);
                              popupState.close();
                            }}
                          >
                            {it.name}
                          </MenuItem>
                        ))}
                      </Menu>
                    </>
                  )}
                </PopupState>
              </Box>
            )}
            {config.openedItems.length ? (
              <Box>
                <Tabs
                  value={openedItem?.id}
                  variant="scrollable"
                  onChange={changeTab}
                >
                  {config.openedItems.map((i, idx) => (
                    <Tab
                      key={i.id}
                      value={i.id}
                      label={
                        <TabLabel
                          name={tabNames?.[idx] ?? 'Undefined'}
                          onClose={() => closeTab(i.id)}
                        />
                      }
                    />
                  ))}
                </Tabs>
              </Box>
            ) : (
              <Box minWidth={400} pr={1.5}>
                <Alert severity="warning">
                  No Wifi/Bluetooth Points Opened
                </Alert>
              </Box>
            )}

            <Box display="flex" flexDirection="column" pr={1.5}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={2}
                flexWrap="wrap"
              >
                <Box display="flex" gap={1}>
                  <Box minWidth={400}>
                    <DateRangeSelect
                      value={
                        openedItem
                          ? [
                              dayjs(openedItem.params?.date_start).toDate(),
                              dayjs(openedItem.params?.date_end).toDate(),
                            ]
                          : undefined
                      }
                      size="small"
                      disabled={!openedItem}
                      onChange={(v) => {
                        if (openedItem) {
                          onUpdate?.(
                            update(config, {
                              openedItems: {
                                [openedItemIndex]: {
                                  $set: {
                                    ...openedItem,
                                    selectAll: true,
                                    params: {
                                      ...openedItem.params,
                                      date_start: v?.[0]
                                        ? dayjs(v?.[0]).format('YYYY-MM-DD')
                                        : undefined,
                                      date_end: v?.[0]
                                        ? dayjs(v?.[1]).format('YYYY-MM-DD')
                                        : undefined,
                                    },
                                  },
                                },
                              },
                            })
                          );
                        }
                      }}
                    />
                  </Box>
                </Box>

                <Box display="flex" sx={{flex: 1, minWidth: 0}}>
                  <Box sx={{flex: 1, minWidth: 0, textAlign: 'right'}}>
                    <AdaptiveButtonGroup disabled={!openedItem}>
                      <Button size="small" data-priority={1}>
                        <WifiPointHistoryTrackingReportExportButton
                          componentProps={{...params}}
                          exportFields={exportFields}
                        />
                      </Button>

                      <Button
                        size="small"
                        onClick={() => params && fetchData(params)}
                        data-priority={2}
                      >
                        <RefreshIcon />
                      </Button>

                      <AutoRefreshSelect
                        value={openedItem?.refreshInterval ?? null}
                        onChange={(v) => {
                          if (openedItem) {
                            onUpdate?.(
                              update(config, {
                                openedItems: {
                                  [openedItemIndex]: {
                                    $set: {
                                      ...openedItem,
                                      refreshInterval: v,
                                    },
                                  },
                                },
                              })
                            );
                          }
                        }}
                        data-priority={3}
                      />
                      <Button
                        size="small"
                        onClick={() => dataGridRef.current?.printTable()}
                        data-priority={4}
                      >
                        <PrintIcon />
                      </Button>
                    </AdaptiveButtonGroup>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          <Backdrop
            open={fetchedInProgress}
            sx={{position: 'absolute', zIndex: 1199}}
          >
            <CircularProgress color="inherit" />
          </Backdrop>

          {fetchedErrors.map((error, idx) => (
            <Alert
              key={idx}
              severity="error"
              onClose={() => params && fetchData(params)}
            >
              {error}
            </Alert>
          ))}

          <DataGrid
            ref={dataGridRef}
            rows={rows}
            columns={columns}
            loading={fetchedInProgress}
            shownFields={shownFields}
            defaultShownFields={DEFAULT_SHOWN_FIELDS}
            columnWidths={openedItem?.columnWidths}
            columnOrder={openedItem?.columnOrder}
            pagination
            paginationMode="server"
            size="small"
            sortBy={
              params?.order
                ? {
                    field: params?.order,
                    dir: params?.dir === 'DESC' ? 'desc' : 'asc',
                  }
                : null
            }
            sortingMode="server"
            page={params?.page}
            pageSize={params?.limit}
            rowCount={fetchedData?.count}
            sxFooter={{
              bgcolor: (theme) =>
                theme.palette.mode === 'dark' ? '#2E2E2E' : '#FFF',
            }}
            onPageChange={(v) => {
              if (openedItem) {
                onUpdate?.(
                  update(config, {
                    openedItems: {
                      [openedItemIndex]: {
                        $set: {
                          ...openedItem,
                          selectAll: true,
                          params: {
                            ...openedItem.params,
                            page: v,
                          },
                        },
                      },
                    },
                  })
                );
              }
            }}
            onPageSizeChange={(v) => {
              if (openedItem) {
                onUpdate?.(
                  update(config, {
                    openedItems: {
                      [openedItemIndex]: {
                        $set: {
                          ...openedItem,
                          selectAll: true,
                          params: {
                            ...openedItem.params,
                            page: 0,
                            limit: v,
                          },
                        },
                      },
                    },
                  })
                );
              }
            }}
            onSort={(v) => {
              if (v && openedItem) {
                onUpdate?.(
                  update(config, {
                    openedItems: {
                      [openedItemIndex]: {
                        $set: {
                          ...openedItem,
                          params: {
                            ...openedItem.params,
                            order: v.field,
                            dir: v.dir === 'desc' ? 'DESC' : 'ASC',
                          },
                        },
                      },
                    },
                  })
                );
              }
            }}
            onShownFieldsChange={handleShownFieldsChange}
            onColumnWidthsChange={handleColumnWidthChange}
            onColumnOrderAndShownFieldsChange={handleColumnOrderAndShownFieldsChange}
          />
        </Box>
      </ResizableColumns>
    </>
  );
};
